import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { type Userdata } from "@/types/user";
import { useToastDefault } from "@/hooks/use-toast-default";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fileToBase64 } from "./helpers";
import { useReactHookFormAxiosValidation } from "@/hooks/use-react-hook-form-axios-validation";
import { FormInput } from "@/components/forms/input";
import { useCreateOrUpdateUserMutation } from "@/api/users-admin";

interface Props {
  user?: Userdata;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

interface FormInputs {
  username: string;
  password: string;
  name: string;
  surname: string;
  email: string;
  phone_number: string;
  avatar: FileList;
  is_active: boolean;
}

export const UserModal: React.FC<Props> = ({ user, isOpen, setIsOpen }) => {
  const { showToast } = useToastDefault();

  const methods = useForm<FormInputs>({
    defaultValues: {
      username: user?.username || "",
      password: "",
      name: user?.name || "",
      surname: user?.surname || "",
      email: user?.email || "",
      phone_number: user?.phoneNumber || "",
      is_active: user ? user.isActive : true,
    },
  });

  const [createUserMutation] = useCreateOrUpdateUserMutation();

  const handleReactHookFormAxiosValidation = useReactHookFormAxiosValidation<FormInputs>();

  const onSubmit = async (payload: FormInputs) => {
    const avatarFile = payload.avatar.item(0);
    const avatar = avatarFile ? (await fileToBase64(avatarFile)).replace(/^data:(.*,)?/, "") : "";

    try {
      await createUserMutation({ ...payload, avatar, id: user?.id }).unwrap();

      setIsOpen(false);
      showToast("Success", user ? "User change" : "User added");
    } catch (error) {
      handleReactHookFormAxiosValidation(error, methods.setError);
    }
  };

  const handleGenerateRandomPassword = () => {
    methods.setValue("password", Math.random().toString(36).slice(-12));
  };

  return (
    <FormProvider {...methods}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <form method="post" onSubmit={methods.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>{user ? "Update user" : "Create user"}</DialogTitle>
            </DialogHeader>

            <FormInput name="username" placeholder="Enter username" className="my-3" />
            <FormInput name="name" placeholder="Enter name" className="my-3" />
            <FormInput name="surname" placeholder="Enter surname" className="my-3" />
            <FormInput name="email" placeholder="Enter email" className="my-3" />
            <FormInput name="phone_number" placeholder="Enter phone number" className="my-3" />
            <FormInput name="password" placeholder="Password (leave empty to not change)" className="mb-1 mt-3" />
            <div className="mb-4 cursor-pointer text-right text-xs text-primary" onClick={handleGenerateRandomPassword}>
              generate random password
            </div>
            <FormInput name="avatar" type="file" className="mb-1 mt-3" accept="image/png, image/jpeg" />
            <div className="items-cent er my-3 flex">
              <input type="checkbox" {...methods.register("is_active")} id="is_active" className="mr-2" />
              <label htmlFor="is_active" className="cursor-pointer text-sm">
                Is active?
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={methods.formState.isSubmitting}>
                {methods.formState.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};
